import { useState, useEffect, useCallback } from "react";
import { SolicitorAlgorithm } from "./solicitor-options";
import { simplePostcodeRegex } from "../../../utils/regexes";
import { SolicitorSuggestion, SolicitorOfficeSuggestion } from "./types";

// Type guard to validate API response
function isSolicitorSuggestion(obj: unknown): obj is SolicitorSuggestion {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "firmId" in obj &&
    typeof obj.firmId === "number" &&
    "name" in obj &&
    typeof obj.name === "string" &&
    "isCharterMember" in obj &&
    typeof obj.isCharterMember === "boolean"
  );
}

function isSolicitorArray(data: unknown): data is (SolicitorSuggestion | SolicitorOfficeSuggestion)[] {
  return Array.isArray(data) && data.every(isSolicitorSuggestion);
}

interface UseSolicitorLookupResult {
  solicitors: (SolicitorSuggestion | SolicitorOfficeSuggestion)[];
  isLoading: boolean;
  error: string | null;
  retry: () => void;
}

export function useSolicitorLookup(
  postcode: string | undefined,
  algorithm: SolicitorAlgorithm | undefined,
  charteredOnly: boolean,
): UseSolicitorLookupResult {
  const [solicitors, setSolicitors] = useState<(SolicitorSuggestion | SolicitorOfficeSuggestion)[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchSolicitors = useCallback(async () => {
    // Only fetch if we have a valid postcode format and algorithm
    if (!postcode || !algorithm || !simplePostcodeRegex.test(postcode)) {
      setSolicitors([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        postcode,
        charteredOnly: charteredOnly.toString(),
      });

      const response = await fetch(`/solicitors/${algorithm.toLowerCase()}?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch solicitors: ${response.statusText}`);
      }

      const data: unknown = await response.json();

      if (!isSolicitorArray(data)) {
        throw new Error("Invalid response format from solicitors API");
      }

      setSolicitors(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while fetching solicitors");
      setSolicitors([]);
    } finally {
      setIsLoading(false);
    }
  }, [postcode, algorithm, charteredOnly, retryCount]);

  useEffect(() => {
    void fetchSolicitors();
  }, [fetchSolicitors]);

  const retry = useCallback(() => {
    setRetryCount((prev) => prev + 1);
  }, []);

  return { solicitors, isLoading, error, retry };
}
